import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import WhiteboardPresentation from '../components/WhiteboardPresentation';
import { 
  BookOpen, CheckCircle, ChevronLeft, ChevronRight, Clock,
  Loader2, Sparkles, RefreshCw, Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { enrollmentsAPI, coursesAPI, modulesAPI, moduleContentAPI } from '../lib/api';
import { toast } from 'sonner';

export default function CoursePlayerPage() {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [enrollment, setEnrollment] = useState(null);
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  
  // AI Content State
  const [moduleContent, setModuleContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  const pollingRef = useRef(null);

  const currentModule = modules[currentModuleIndex];
  const isModuleCompleted = enrollment?.completed_modules?.includes(currentModule?.id);

  useEffect(() => {
    fetchData();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [enrollmentId]);

  useEffect(() => {
    if (modules.length > 0 && currentModuleIndex >= 0) {
      fetchModuleContent();
    }
  }, [currentModuleIndex, modules]);

  const fetchData = async () => {
    try {
      const enrollmentRes = await enrollmentsAPI.getById(enrollmentId);
      setEnrollment(enrollmentRes.data);
      const courseRes = await coursesAPI.getById(enrollmentRes.data.course_id);
      setCourse(courseRes.data);
      const modulesRes = await modulesAPI.getByCourse(enrollmentRes.data.course_id);
      setModules(modulesRes.data);
      const completedModules = enrollmentRes.data.completed_modules || [];
      const firstIncomplete = modulesRes.data.findIndex(m => !completedModules.includes(m.id));
      setCurrentModuleIndex(firstIncomplete >= 0 ? firstIncomplete : 0);
    } catch (error) {
      toast.error('Failed to load course');
      navigate('/my-learning');
    } finally {
      setLoading(false);
    }
  };

  const fetchModuleContent = async () => {
    const module = modules[currentModuleIndex];
    if (!module) return;
    
    setContentLoading(true);
    setModuleContent(null);
    setGenerationStatus(null);
    
    try {
      const response = await moduleContentAPI.getContent(module.id);
      if (response.data.content_exists || response.data.whiteboard_slides?.length > 0) {
        setModuleContent(response.data);
      }
    } catch (error) {
      console.log('No existing content');
    } finally {
      setContentLoading(false);
    }
  };

  const generateContent = async () => {
    const module = modules[currentModuleIndex];
    if (!module) return;
    
    setContentLoading(true);
    setGenerationStatus({ status: 'starting', progress: 'Starting generation...' });
    
    try {
      // Start async generation
      await moduleContentAPI.generateContentAsync(module.id, false);
      
      // Poll for status
      pollingRef.current = setInterval(async () => {
        try {
          const statusRes = await moduleContentAPI.getGenerationStatus(module.id);
          setGenerationStatus(statusRes.data);
          
          if (statusRes.data.status === 'completed') {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            // Fetch the generated content
            const contentRes = await moduleContentAPI.getContent(module.id);
            setModuleContent(contentRes.data);
            setContentLoading(false);
            toast.success('Lesson ready! Click "Start Lesson" to begin.');
          } else if (statusRes.data.status === 'failed') {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setContentLoading(false);
            toast.error('Generation failed: ' + (statusRes.data.error || 'Unknown error'));
          }
        } catch (e) {
          console.error('Polling error:', e);
        }
      }, 3000);
      
    } catch (error) {
      setContentLoading(false);
      toast.error('Failed to start generation');
      console.error(error);
    }
  };

  const handleModuleComplete = async () => {
    try {
      await enrollmentsAPI.completeModule(enrollment.id, currentModule.id);
      const enrollmentRes = await enrollmentsAPI.getById(enrollment.id);
      setEnrollment(enrollmentRes.data);
      toast.success('Module completed! 🎉');
      
      // Auto-advance to next module
      if (currentModuleIndex < modules.length - 1) {
        setTimeout(() => {
          setCurrentModuleIndex(prev => prev + 1);
        }, 1500);
      }
    } catch (error) {
      toast.error('Failed to mark module complete');
    }
  };

  // Pre-generate next module when current module starts playing
  const preGenerateNextModule = async () => {
    const nextIndex = currentModuleIndex + 1;
    if (nextIndex >= modules.length) return; // No next module
    
    const nextModule = modules[nextIndex];
    if (!nextModule) return;
    
    console.log(`Pre-generating next module: ${nextModule.title}`);
    
    try {
      // Check if next module already has content
      const contentCheck = await moduleContentAPI.getContent(nextModule.id);
      if (contentCheck.data.content_exists || contentCheck.data.whiteboard_slides?.length > 0) {
        console.log('Next module already has content, skipping pre-generation');
        return;
      }
    } catch (e) {
      // No content exists, proceed with generation
    }
    
    try {
      // Start async generation for next module (in background)
      await moduleContentAPI.generateContentAsync(nextModule.id, false);
      console.log(`Started pre-generation for: ${nextModule.title}`);
      
      // We don't poll here - the content will be ready when user navigates to it
      // If not ready, the normal generation flow will handle it
    } catch (error) {
      console.log('Pre-generation request failed, will generate on demand:', error.message);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#0056D2]" />
        </div>
      </Layout>
    );
  }

  if (!course || modules.length === 0) {
    return (
      <Layout>
        <div className="text-center py-12">
          <BookOpen className="w-20 h-20 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900">No modules available</h2>
          <p className="text-slate-500 mt-2">This course doesn't have any content yet.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="course-player">
        {/* Course Header */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-[#0056D2] text-white text-xs">Professional</Badge>
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">{course.title}</h1>
              <p className="text-slate-500">
                Module {currentModuleIndex + 1} of {modules.length}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-slate-500">Course Progress</p>
                <p className="text-2xl font-bold text-[#0056D2]">{Math.round(enrollment?.progress || 0)}%</p>
              </div>
              <div className="w-24 h-24 relative">
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="48" cy="48" r="40" fill="none" stroke="#E2E8F0" strokeWidth="8" />
                  <circle 
                    cx="48" cy="48" r="40" fill="none" 
                    stroke="#0056D2" strokeWidth="8"
                    strokeDasharray={`${(enrollment?.progress || 0) * 2.51} 251`}
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-3 space-y-6">
            {/* Module Header */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="font-heading text-xl font-bold text-slate-900">{currentModule?.title}</h2>
                  <p className="text-slate-500 mt-1">{currentModule?.description}</p>
                </div>
                {isModuleCompleted && (
                  <Badge className="bg-green-100 text-green-700">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Completed
                  </Badge>
                )}
              </div>

              {/* Content Generation Status */}
              {!moduleContent && !contentLoading && (
                <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-[#0056D2] rounded-xl flex items-center justify-center">
                      <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">AI-Powered Lesson</p>
                      <p className="text-sm text-slate-600">
                        Generate an interactive whiteboard presentation with audio narration
                      </p>
                    </div>
                    <Button 
                      onClick={generateContent} 
                      className="bg-[#0056D2] hover:bg-[#00419E] px-6"
                      data-testid="generate-content-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Lesson
                    </Button>
                  </div>
                </div>
              )}

              {/* Generation Progress */}
              {contentLoading && generationStatus && (
                <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <div className="flex items-center gap-4">
                    <Loader2 className="w-8 h-8 animate-spin text-[#0056D2]" />
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">Generating Lesson...</p>
                      <p className="text-sm text-[#0056D2]">{generationStatus.progress}</p>
                    </div>
                  </div>
                  <div className="mt-4 h-2 bg-white rounded-full overflow-hidden">
                    <div className="h-full bg-[#0056D2] rounded-full animate-pulse" style={{ width: '60%' }} />
                  </div>
                </div>
              )}
            </div>

            {/* Whiteboard Presentation */}
            {moduleContent && moduleContent.whiteboard_slides?.length > 0 && (
              <WhiteboardPresentation 
                slides={moduleContent.whiteboard_slides}
                quiz={moduleContent.quiz}
                onComplete={() => {
                  if (!isModuleCompleted) {
                    handleModuleComplete();
                  }
                }}
                onModuleStarted={preGenerateNextModule}
              />
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <Button
                variant="outline"
                onClick={() => setCurrentModuleIndex(prev => Math.max(0, prev - 1))}
                disabled={currentModuleIndex === 0}
                className="border-slate-200"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
              
              <span className="text-sm text-slate-500">
                {currentModuleIndex + 1} / {modules.length}
              </span>

              <Button
                onClick={() => setCurrentModuleIndex(prev => Math.min(modules.length - 1, prev + 1))}
                disabled={currentModuleIndex === modules.length - 1}
                className="bg-[#0056D2] hover:bg-[#00419E]"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>

          {/* Sidebar - Module List */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4">Course Modules</h3>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {modules.map((module, idx) => {
                  const completed = enrollment?.completed_modules?.includes(module.id);
                  const current = idx === currentModuleIndex;
                  
                  return (
                    <button
                      key={module.id}
                      onClick={() => setCurrentModuleIndex(idx)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        current 
                          ? 'border-[#0056D2] bg-blue-50' 
                          : completed 
                            ? 'border-green-200 bg-green-50' 
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                          completed ? 'bg-green-500 text-white' : current ? 'bg-[#0056D2] text-white' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {completed ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                        </div>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium truncate ${current ? 'text-[#0056D2]' : 'text-slate-700'}`}>
                            {module.title}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Clock className="w-3 h-3 text-slate-400" />
                            <span className="text-xs text-slate-500">{module.duration_minutes || 15} min</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
